"use client";

import { AlertCircle, RefreshCcw } from "lucide-react";
import posthog from "posthog-js";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Global error caught by boundary:", error);

    posthog.captureException(error, {
      digest: error.digest,
      boundary: "global-error",
    });

    // Automatic reload for ChunkLoadError
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("CSS chunk");

    if (isChunkError) {
      const lastReload = localStorage.getItem("last_chunk_reload");
      const now = Date.now();
      if (!lastReload || now - Number.parseInt(lastReload, 10) > 10000) {
        localStorage.setItem("last_chunk_reload", now.toString());
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <html lang="en" className="dark">
      <body className="bg-background text-foreground antialiased min-h-screen flex items-center justify-center font-sans overflow-hidden">
        {/* Background Decorations */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#0193cd 1px, transparent 1px), linear-gradient(90deg, #0193cd 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/5 blur-[150px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-md w-full mx-4 p-8 rounded-3xl border border-border bg-card/50 backdrop-blur-xl shadow-2xl space-y-6 text-center">
          <div className="mx-auto bg-primary/10 p-4 rounded-2xl w-16 h-16 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-primary" />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-black tracking-tight">Something went wrong</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We encountered a critical application error. A reload should fix it, but if the issue
              persists, feel free to contact us.
            </p>
          </div>

          {process.env.NODE_ENV === "development" && (
            <div className="w-full p-4 bg-muted/50 rounded-xl text-left overflow-auto max-h-40 font-mono text-xs text-red-500 border border-red-500/10">
              <p className="font-bold mb-1">
                {error.name}: {error.message}
              </p>
              {error.stack && <pre className="opacity-70">{error.stack}</pre>}
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={() => window.location.reload()}
              className="w-full rounded-full gap-2 py-6 text-base font-medium shadow-lg shadow-primary/20 cursor-pointer"
            >
              <RefreshCcw className="w-5 h-5" />
              Reload Page
            </Button>
            <Button
              variant="ghost"
              onClick={reset}
              className="w-full rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
            >
              Try recovering
            </Button>
          </div>
        </div>
      </body>
    </html>
  );
}
